import { request } from '../api/client';

/** Quyền hiệu lực của người đang đăng nhập. */
export interface MyPermissions {
  readonly permissionCodes: readonly string[];
  readonly hasCompanyWideDataScope: boolean;
}

/**
 * Mã quyền mà giao diện cần biết.
 *
 * Gom thành hằng số thay vì viết chuỗi tại chỗ dùng. Một mã viết sai chính tả sẽ làm mục thực đơn
 * biến mất với tất cả mọi người, và không có gì báo lỗi — thứ khó phát hiện nhất là một chức năng
 * đơn giản là không xuất hiện.
 */
export const PermissionCodes = {
  /** Xem danh sách vai trò và quyền của chúng. */
  RoleView: 'PLT.role.view',

  /** Quản trị vai trò: lập, sửa, ngừng dùng. */
  RoleManage: 'PLT.role.manage',

  /** Xem việc gán quyền và quy tắc phạm vi dữ liệu. */
  PermissionAssignmentView: 'PLT.permission_assignment.view',

  /** Cấp và thu hồi quyền, lập và xóa quy tắc phạm vi dữ liệu. */
  PermissionAssignmentManage: 'PLT.permission_assignment.manage',

  /** Xem danh sách tài khoản người dùng. */
  UserAccountView: 'PLT.user_account.view',

  /** Lập tài khoản mới. */
  UserAccountCreate: 'PLT.user_account.create',

  /** Sửa tài khoản. */
  UserAccountUpdate: 'PLT.user_account.update',

  /** Quản trị tài khoản: vô hiệu hóa, kích hoạt lại, gán và thu hồi vai trò. */
  UserAccountManage: 'PLT.user_account.manage',

  /** Đặt lại mật khẩu người khác. */
  UserAccountResetPassword: 'PLT.user_account.reset_password',

  /** Xem danh mục đơn vị đo. */
  UnitOfMeasureView: 'PLT.unit_of_measure.view',

  /** Quản trị danh mục đơn vị đo. */
  UnitOfMeasureManage: 'PLT.unit_of_measure.manage',

  /** Xem danh mục tiền tệ. */
  CurrencyView: 'PLT.currency.view',

  /** Quản trị danh mục tiền tệ. */
  CurrencyManage: 'PLT.currency.manage',

  /** Xem danh mục quốc gia. */
  CountryView: 'PLT.country.view',

  /** Quản trị danh mục quốc gia. */
  CountryManage: 'PLT.country.manage',

  /** Xem danh mục thuế suất. */
  TaxRateView: 'PLT.tax_rate.view',

  /** Quản trị danh mục thuế suất. */
  TaxRateManage: 'PLT.tax_rate.manage',

  /** Xem khách hàng. */
  CustomerView: 'SAL.customer.view',

  /** Khai khách hàng mới. */
  CustomerCreate: 'SAL.customer.create',

  /** Sửa thông tin chung của khách hàng. */
  CustomerUpdate: 'SAL.customer.update',

  /** Đổi mã khách hàng. Quyền riêng vì mã đã xuất hiện trên chứng từ đã gửi đi. */
  CustomerChangeCode: 'SAL.customer.change_code',

  /** Sửa thông tin in trên hóa đơn: mã số thuế, địa chỉ hóa đơn, quốc gia. */
  CustomerUpdateInvoiceIdentity: 'SAL.customer.update_invoice_identity',

  /** Sửa điều khoản thương mại: hạn mức công nợ, số ngày thanh toán. */
  CustomerUpdateCommercialTerms: 'SAL.customer.update_commercial_terms',

  /** Chỉ định người phụ trách khách hàng. */
  CustomerAssignOwner: 'SAL.customer.assign_owner',

  /** Đổi trạng thái quan hệ với khách hàng. */
  CustomerChangeStatus: 'SAL.customer.change_status',

  /** Di chuyển khách hàng trong cây. */
  CustomerMove: 'SAL.customer.move',

  /** Xóa khách hàng. */
  CustomerDelete: 'SAL.customer.delete',

  /** Xem cơ hội bán hàng và bản tổng hợp phễu. */
  OpportunityView: 'SAL.opportunity.view',

  /** Khai, sửa và chuyển giai đoạn cơ hội bán hàng. */
  OpportunityManage: 'SAL.opportunity.manage',

  /** Xem liên hệ khách để lại ở form trên website. */
  ContactRequestView: 'SAL.contact_request.view',

  /** Ghi kết quả liên hệ lại với khách từ website. */
  ContactRequestManage: 'SAL.contact_request.manage',

  /** Xem dự án. */
  ProjectView: 'PRJ.project.view',

  /** Mở dự án mới. */
  ProjectCreate: 'PRJ.project.create',

  /** Sửa tên, mô tả và mức độ ưu tiên của dự án. */
  ProjectUpdate: 'PRJ.project.update',

  /** Sửa lịch trình, gồm cả mốc cam kết trong hợp đồng. */
  ProjectUpdateSchedule: 'PRJ.project.update_schedule',

  /** Cập nhật tiến độ và tình trạng sức khỏe. */
  ProjectUpdateProgress: 'PRJ.project.update_progress',

  /** Đổi trạng thái dự án, gồm cả đóng và hủy. */
  ProjectChangeStatus: 'PRJ.project.change_status',

  /** Chỉ định người quản lý dự án và những người phụ trách khác. */
  ProjectAssignManager: 'PRJ.project.assign_manager',

  /** Ghi nhận nghiệm thu và chốt hạn bảo hành. */
  ProjectRecordAcceptance: 'PRJ.project.record_acceptance',

  /** Xóa dự án. */
  ProjectDelete: 'PRJ.project.delete',

  /** Xem danh sách thành viên dự án và bảng phân bổ nguồn lực. */
  ProjectMemberView: 'PRJ.project_member.view',

  /** Thêm, sửa và cho rời thành viên dự án. */
  ProjectMemberManage: 'PRJ.project_member.manage',

  /** Xem cây công việc của dự án. */
  ProjectTaskView: 'PRJ.project_task.view',

  /** Lập, sửa, sắp xếp và xóa công việc, kể cả quan hệ phụ thuộc. */
  ProjectTaskManage: 'PRJ.project_task.manage',

  /** Báo tiến độ và đổi trạng thái công việc. */
  ProjectTaskUpdateProgress: 'PRJ.project_task.update_progress',

  /** Xem đường mốc của dự án. */
  ProjectMilestoneView: 'PRJ.project_milestone.view',

  /** Khai, sửa, dời lịch và xóa mốc dự án. */
  ProjectMilestoneManage: 'PRJ.project_milestone.manage',

  /**
   * Ghi nhận một mốc đã đạt.
   *
   * Tách khỏi quyền quản lý mốc vì với mốc gắn đợt thanh toán thì đây là căn cứ xuất hóa đơn.
   */
  ProjectMilestoneRecord: 'PRJ.project_milestone.record',

  /** Xem mẫu dự án và cây công việc trong mẫu. */
  ProjectTemplateView: 'PRJ.project_template.view',

  /** Khai và sửa mẫu dự án — tức là khai quy trình chuẩn của công ty. */
  ProjectTemplateManage: 'PRJ.project_template.manage',

  /**
   * Áp mẫu vào một dự án.
   *
   * Tách khỏi quyền sửa mẫu: mọi người quản lý dự án đều cần áp mẫu vì đó là thao tác của việc mở dự
   * án mới, còn số người được sửa quy trình chuẩn thì rất ít.
   */
  ProjectTemplateApply: 'PRJ.project_template.apply',

  /** Xem danh mục loại dự án. */
  ProjectTypeView: 'PRJ.project_type.view',

  /** Quản trị danh mục loại dự án. */
  ProjectTypeManage: 'PRJ.project_type.manage',

  /** Xem danh sách dự án tiêu biểu — nội dung trang giới thiệu trên website công ty. */
  ShowcaseProjectView: 'PRJ.showcase_project.view',

  /** Thêm, sửa, ẩn hiện và xóa dự án tiêu biểu. */
  ShowcaseProjectManage: 'PRJ.showcase_project.manage',

  /** Xem bài viết và danh mục blog của website công ty, kể cả bản nháp. */
  BlogView: 'PRJ.blog.view',

  /** Viết, sửa, đăng, gỡ, xóa bài viết; quản lý danh mục blog. */
  BlogManage: 'PRJ.blog.manage',
} as const;

/** Lấy quyền hiệu lực của chính người đang đăng nhập. */
export async function fetchMyPermissions(): Promise<MyPermissions> {
  return await request<MyPermissions>('/api/v1/authorization/my-permissions');
}
